import {makeApiCall} from "../../hooks/CancellableApiCall";
import {LoadingContent} from "../LoadingContent";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
    Box,
    Checkbox,
    FormControlLabel,
    Link,
    Pagination,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField
} from "@mui/material";
import {Link as RouterLink} from "react-router-dom";
import {formatDate} from "../../utils";
import {DivisionApplicationSummary} from "../../data/DivisionApplicationSummary";
import {PoliceUser} from "../PoliceUser";
import useApiCall from "../../hooks/CancellableApiCall";

interface DivisionApplicationPaginatedResponse {
    totalCount: number
    totalPages: number
    applications: DivisionApplicationSummary[]
}

interface ApplicationsProps {
    divisionIdentifier: string
}

interface ApplicationsRowProps {
    application: DivisionApplicationSummary
    divisionIdentifier: string
}

const ApplicationsRow = (props: ApplicationsRowProps) => {
    const [cooldown, setCooldown] = useState(props.application.isCooldownVoided)

    return <TableRow>
        <TableCell>
            <Link underline="none" component={RouterLink} to={`/careers/mark/${props.application.id}/${props.application.communityId}`}>
                {props.application.id}
            </Link>
        </TableCell>
        <TableCell>
            <PoliceUser user={{nick: props.application.nick, communityId: props.application.communityId}} rank={props.application.policeRank}/>
        </TableCell>
        <TableCell>{props.application.roleDisplayName}</TableCell>
        <TableCell>{formatDate(props.application.dateFailed)}</TableCell>
        <TableCell>
            <FormControlLabel label={"Voided"} control={
                <Checkbox onChange={(_) => {
                    makeApiCall({
                        url: `/api/division/${props.divisionIdentifier}/applications/cooldown`,
                        method: "PATCH",
                        body: {
                            applicationId: props.application.userRoleApplicationId,
                            cooldown: !cooldown
                        },
                        onLoadedCallback: () => {
                            setCooldown(!cooldown)
                        },
                        onError: () => {}
                    })
                }}
                          checked={cooldown}/>
            }/>
        </TableCell>
    </TableRow>
}

export const Applications = (props: ApplicationsProps) => {
    const [query, setQuery] = useState("")
    const [debouncedQuery, setDebouncedQuery] = useState("")
    const [page, setPage] = useState(1)
    const debounceTimer = useRef<NodeJS.Timeout | null>(null)

    const handleQueryChange = useCallback((value: string) => {
        setQuery(value)
        if (debounceTimer.current) clearTimeout(debounceTimer.current)
        debounceTimer.current = setTimeout(() => {
            setDebouncedQuery(value)
            setPage(1)
        }, 400)
    }, [])

    useEffect(() => {
        return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current) }
    }, [])

    const url = useMemo(() => {
        const params = new URLSearchParams()
        params.set("page", String(page))
        if (debouncedQuery) params.set("query", debouncedQuery)
        return `/api/division/${props.divisionIdentifier}/applications?${params.toString()}`
    }, [page, debouncedQuery, props.divisionIdentifier])

    const data = useApiCall<DivisionApplicationPaginatedResponse>({url})
    const totalPages = Math.max(1, data.data?.totalPages ?? 1)

    return <Stack spacing={2}>
        <TextField
            fullWidth
            variant="standard"
            label="Search by name or community ID..."
            value={query}
            onChange={(e) => handleQueryChange(e.target.value)}
        />
        <Box sx={{overflowX: "auto"}}>
            <LoadingContent showLoadingSkeleton isLoading={data.isLoading}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Application ID</TableCell>
                            <TableCell>User</TableCell>
                            <TableCell>Role Applied For</TableCell>
                            <TableCell>Date Failed</TableCell>
                            <TableCell>Cooldown</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.data?.applications?.map((application) =>
                            <ApplicationsRow key={application.id} divisionIdentifier={props.divisionIdentifier} application={application}/>
                        )}
                    </TableBody>
                </Table>
            </LoadingContent>
        </Box>
        <Box sx={{display: "flex", justifyContent: "center"}}>
            <Pagination color="secondary" count={totalPages} page={page} onChange={(_, v) => setPage(v)}/>
        </Box>
    </Stack>
}
